import "../navigation.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Business } from "@mui/icons-material";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import { Drawer } from "@mui/material";
import BankCard from "assets/img/svg/bankCard";
import DrawIcon from "assets/img/svg/drawIcon";
import FileSuccess from "assets/img/svg/fileSuccess";
import Beneficiaries from "assets/img/svg/navbar/beneficiaries";
import Management from "assets/img/svg/navbar/management";
import Settings from "assets/img/svg/navbar/settings";
import Titles from "assets/img/svg/navbar/titles";
import Wallet from "assets/img/svg/navbar/wallet";
import Transfer from "assets/img/svg/transfer";
import { getPendingImports } from "components/ImportData/utils";
import NotificationCenter from "components/NotificationCenter";
import { getLateDeclarations } from "containers/DeclarationPage/utils";
import { PortalType } from "contexts/portal.context";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import useCrisp from "hooks/useCrisp";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchImportedFiles } from "store/filesHistorySlice";
import { fetchCompanyInfo } from "store/settingsStore";
import type { RootState } from "store/store";
import { useAppDispatch, useAppSelector } from "store/store";

import { useAuth } from "../../../../contexts/auth/AuthContext";
import RolePreview from "../../RolePreview";
import {
    CollapseButton,
    HelpMenu,
    NavigationBlockWithBadge,
    NavigationHeader,
    NavigationLayout,
    NavigationLink,
    NavigationProfile,
    NotificationsButton,
    PendingImportsButton,
} from "../components";
import { NavigationProvider, useNavigationCollapse } from "../context";
import type { NavigationLinkConfig } from "../types";

type TNavbarList = {
    groupLabel: string;
    groupClass: string;
    display: boolean;
    children: {
        label: string;
        link: string;
        pageName: string;
        icon: JSX.Element;
        show: boolean;
        customComponent?: JSX.Element;
        disabled?: boolean;
        disabledTooltip?: string;
    }[];
};

/**
 * Inner navigation component that uses the collapse context
 */
const BookNavigationContent = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const portalType = useContext(PortalType);
    const { isCollapsed } = useNavigationCollapse();
    const ability = useAbility();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { notifications } = useSelector((state: RootState) => state.notification);
    const { companySettings, userData, rolePreview } = useSelector((state: RootState) => state.settings);
    const { contributionList } = useAppSelector((state) => state.contribution);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);
    const hasMultipleCompanies = userData?.companies && userData?.companies.length > 1;

    const { user, signOut, authStatus } = useAuthenticator((context) => [context.user]);
    const accountType = user.attributes && user.attributes["custom:accountType"];
    const isAdminProd = accountType == "admin" && window.location.host === "manage.crealo.app";

    const { handleSwitchCompany, getCompanySettings, companyInfos, countryFeatures } = useCompanySettings();
    const usesValidationChain = getCompanySettings("uses_agreement_validation_chain");
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");
    const usesRightsTransfer = getCompanySettings("uses_transfer_term");
    const usesAnalytics = getCompanySettings("uses_analytics");
    const usesUrssafDeclarations = getCompanySettings("uses_urssaf_declarations") ?? true;
    const isCompanyBelgian = companyInfos?.countryCode === "fr-BE";
    const { isAdminCrealo: isCrealoAdmin } = useAuth();

    // Page access permissions (respecting company-level usesAgreementsOnly feature flag)
    const canAccessAnalytics = !usesAgreementsOnly && ability.can("access", "AnalyticsPage");
    const canAccessManagement = !usesAgreementsOnly && ability.can("access", "ManagementPage");
    const canAccessAdvancePayment = !usesAgreementsOnly && ability.can("access", "AdvancePaymentPage");
    const canAccessTransferTerms = !usesAgreementsOnly && ability.can("access", "TransferTermsPage");
    const canAccessPayment = !usesAgreementsOnly && ability.can("access", "PaymentPage");
    const canAccessDeclarations =
        !usesAgreementsOnly &&
        countryFeatures.urssaf &&
        usesUrssafDeclarations &&
        ability.can("access", "DeclarationsPage");
    const canAccessTransferees = !usesAgreementsOnly && ability.can("access", "TransfereesPage");
    const canAccessTitles = ability.can("access", "TitlesPage");
    const canAccessBeneficiars = ability.can("access", "BeneficiarsPage");
    const canAccessAgreements = ability.can("access", "AgreementsPage");
    const showAgreements = (usesValidationChain || !!usesAgreements) && canAccessAgreements;

    const numberLateDeclarations = getLateDeclarations(contributionList).length;

    useEffect(() => {
        if (authStatus == "unauthenticated") window.location.reload();
    }, [authStatus]);

    const handleLogOut = () => {
        dispatch({ type: "USER/LOGOUT" });
        signOut();
    };

    // Crisp Hook
    useCrisp(portalType);

    useEffect(() => {
        if (!companySettings) dispatch(fetchCompanyInfo());
    }, [companySettings, dispatch]);

    const declarationChip = () => (
        <NavigationBlockWithBadge
            icon={<FileSuccess color="currentColor" />}
            label={t("NavigationBar.Declarations")}
            badgeCount={numberLateDeclarations}
            badgeTooltip={t("NavigationBar.LateDeclarations", { count: numberLateDeclarations })}
            isCollapsed={isCollapsed}
        />
    );

    const navbarList: TNavbarList[] = [
        {
            groupLabel: "",
            groupClass: "navigation-main",
            display: true,
            children: [
                {
                    label: "NavigationBar.Analytics",
                    link: "/dashboard",
                    pageName: "analytics",
                    icon: <LeaderboardOutlinedIcon />,
                    show: usesAnalytics && canAccessAnalytics,
                    // disabled: true, // temporarly disabled (maintenance)
                    disabledTooltip: t("NavigationBar.TemporarilyUnavailable"),
                },
                {
                    label: "NavigationBar.Management",
                    link: "/management",
                    pageName: "management",
                    icon: <Management />,
                    show: canAccessManagement,
                },
                {
                    label: "NavigationBar.AdvancePayment",
                    link: "/advance-payment",
                    pageName: "advance-payment",
                    icon: <BankCard />,
                    show: canAccessAdvancePayment,
                },
                {
                    label: "TransferTerms.Terms",
                    link: "/transfer-terms",
                    pageName: "transfer-terms",
                    icon: <Transfer />,
                    show: usesRightsTransfer && canAccessTransferTerms,
                },
                {
                    label: "NavigationBar.Payment",
                    link: "/payment",
                    pageName: "payment",
                    icon: <Wallet />,
                    show: canAccessPayment,
                },
                {
                    label: "NavigationBar.Declarations",
                    link: "/declarations",
                    pageName: "declarations",
                    icon: <FileSuccess color="currentColor" />,
                    show: !isCompanyBelgian && canAccessDeclarations,
                    customComponent: declarationChip(),
                },
            ],
        },
        {
            groupLabel: "NavigationBar.FollowUp",
            groupClass: "navigation-middle",
            display: showAgreements,
            children: [
                {
                    label: "NavigationBar.ContractManagement",
                    link: "/agreements",
                    pageName: "agreements",
                    icon: <DrawIcon />,
                    show: showAgreements,
                },
            ],
        },
        {
            groupLabel: "NavigationBar.Data",
            groupClass: "navigation-middle",
            display: true,
            children: [
                {
                    label: "NavigationBar.Works",
                    link: "/works",
                    pageName: "works",
                    icon: <Titles />,
                    show: true,
                    disabled: !canAccessTitles,
                },
                {
                    label: "NavigationBar.Beneficiars",
                    link: "/beneficiars",
                    pageName: "beneficiaries",
                    icon: <Beneficiaries />,
                    show: true,
                    disabled: !canAccessBeneficiars,
                },
                {
                    label: "Transferees.Transferees",
                    link: "/transferees",
                    pageName: "transferees",
                    icon: <Business width={20} height={20} sx={{ ".MuiSvgIcon-root": { fontSize: "1.2em" } }} />,
                    show: usesRightsTransfer && canAccessTransferees,
                },
                {
                    label: "AdminPanel.AdminPanel",
                    link: "/admin-panel/companiesList",
                    pageName: "admin-panel",
                    icon: <Settings />,
                    show: accountType === "admin",
                },
            ],
        },
    ];

    const notificationsNumber = notifications.filter((notif) => !notif.seen).length;

    const pendingJobs: number = getPendingImports({
        jobs,
        fakeImports: pendingImports,
        type: ["title", "beneficiar", "terms"],
    }).length;

    const handlePendingJobs = async () => {
        navigate("/settings/history");
        await dispatch(fetchImportedFiles({ options: {} }));
    };
    const isOnImportsHistoryPage = window.location.href.includes("settings/history");

    const topContent = (
        <>
            <NavigationHeader isCollapsed={isCollapsed} />

            {navbarList.map((group) => (
                <div className={group.groupClass} key={group.groupLabel}>
                    {group.groupLabel && group.display && <h4>{t(group.groupLabel)}</h4>}
                    {group.children.map((page) => {
                        if (!page.show) return null;

                        const linkConfig: NavigationLinkConfig = {
                            to: page.link,
                            icon: page.icon,
                            label: page.label,
                            disabled: page.disabled,
                            disabledTooltip: page.disabledTooltip,
                            customComponent: page.customComponent,
                        };

                        return (
                            <NavigationLink
                                key={page.pageName}
                                config={linkConfig}
                                variant="book"
                                isCollapsed={isCollapsed}
                            />
                        );
                    })}
                </div>
            ))}
        </>
    );

    const settingsLink: NavigationLinkConfig = {
        to: "/settings",
        icon: <Settings />,
        label: "NavigationBar.Settings",
    };

    const bottomContent = (
        <>
            <HelpMenu portalType="book" hasNavLinks={false} variant="book" isCollapsed={isCollapsed} />

            <NotificationsButton
                unreadCount={notificationsNumber}
                onClick={() => setIsOpen(!isOpen)}
                variant="book"
                isCollapsed={isCollapsed}
            />

            <PendingImportsButton
                pendingCount={pendingJobs}
                onClick={handlePendingJobs}
                isOnHistoryPage={isOnImportsHistoryPage}
                variant="book"
                isCollapsed={isCollapsed}
            />

            <NavigationLink config={settingsLink} variant="book" isCollapsed={isCollapsed} />

            {rolePreview && isCrealoAdmin && <RolePreview role={rolePreview.role} label={rolePreview.label} />}

            <NavigationProfile
                userName={userData?.firstname ?? user.attributes?.name}
                companyName={companySettings?.name}
                avatarUrl={companySettings?.avatarUrl}
                portalType="book"
                showCompanySwitcher={hasMultipleCompanies && !isAdminProd}
                companies={userData?.companies}
                currentCompanyId={companySettings?.id}
                onSwitchCompany={handleSwitchCompany}
                onLogout={handleLogOut}
                isCollapsed={isCollapsed}
            />
        </>
    );

    return (
        <>
            <NavigationLayout
                variant="book"
                isMobile={false}
                isOpen={true}
                isCollapsed={isCollapsed}
                topContent={topContent}
                bottomContent={bottomContent}
                collapseButton={<CollapseButton />}
            />

            <Drawer
                anchor="bottom"
                open={isOpen}
                className="notification-wrapper"
                onClick={() => setIsOpen(false)}
                sx={{ ".MuiPaper-root": { border: "none" } }}
            >
                <NotificationCenter close={() => setIsOpen(false)} />
            </Drawer>
        </>
    );
};

/**
 * Navigation component for Book portal with collapse support
 */
const BookNavigation = () => {
    return (
        <NavigationProvider>
            <BookNavigationContent />
        </NavigationProvider>
    );
};

export default BookNavigation;
